import { useGeckoPermissions } from 'features/permissions'
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import { getOrgMemberPermission, RolodexPermissionsValues } from 'types'

import { TermsModal } from './TermsModal'

const UCAR_ORG_ID = 'ucar-37a0ea'
export const PERMISSIONS_REQUIRING_TERMS: RolodexPermissionsValues[] = [
  getOrgMemberPermission(UCAR_ORG_ID),
]

interface ToSModalContextType {
  showToSModal: boolean
  setShowToSModal: (value: boolean) => void
}
const ToSModalContext = createContext<ToSModalContextType | undefined>(
  undefined
)

export const TermsModalProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [showToSModal, setShowToSModal] = useState<boolean>(false)
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false)

  const { permissions } = useGeckoPermissions()
  const requiredToAccept = PERMISSIONS_REQUIRING_TERMS.some((perm) =>
    permissions?.has(perm)
  )

  useEffect(() => {
    const accepted = localStorage.getItem('termsAccepted')
    if (accepted) {
      setTermsAccepted(true)
    } else if (requiredToAccept) {
      setShowToSModal(true)
    }
  }, [requiredToAccept, setShowToSModal])

  const handleAccept = () => {
    localStorage.setItem('termsAccepted', 'true')
    setTermsAccepted(true)
    setShowToSModal(false)
  }

  const handleClose = () => {
    setShowToSModal(false)
  }

  const needsToAccept = requiredToAccept && !termsAccepted

  return (
    <ToSModalContext.Provider value={{ showToSModal, setShowToSModal }}>
      {children}
      {(showToSModal || needsToAccept) && (
        <TermsModal
          requiredToAccept={needsToAccept}
          onAccept={handleAccept}
          onClose={handleClose}
        />
      )}
    </ToSModalContext.Provider>
  )
}

export const useTermsModal = (): ToSModalContextType => {
  const context = useContext(ToSModalContext)
  if (!context) {
    throw new Error('useToSModal must be used within a ToSModalProvider')
  }
  return context
}
